import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { useDragAndDrop } from '@formkit/drag-and-drop/react';
import { type FormElement } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnDef } from '@tanstack/react-table';

import { LogicRuleConfigLabels } from '@/components/form/config-field-labels';
import ColumnsIcon from '@/components/icons/columns';
import GripDotVerticalIcon from '@/components/icons/grip-dot-vertical';
import { LogicIcon } from '@/components/icons/logic';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { ModuleTableColumn } from '@/stores/modules.store';

import { groupConditionalColumns, reorderWithinSlots, ruleGroupsByColumn, type RuleGroup } from './conditional-column-group';
import { TablePageHeaderActionButton } from './table-page-header';

function ColumnsDialog<T>({
  open,
  onOpenChange,
  columns,
  columnOrder,
  onColumnOrderChange,
  noTrigger,
  disabled,
  defaultSectionLabel,
  formElements,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: ColumnDef<T>[];
  columnOrder: { id: string; active: boolean }[];
  onColumnOrderChange: (columnOrder: { id: string; active: boolean }[]) => void;
  noTrigger?: boolean;
  disabled?: boolean;
  /** Section label for columns that carry no `meta.columnGroup` of their own. */
  defaultSectionLabel?: string;
  /** The form's fields, passed only when conditional logic is on, to group revealed fields under their rule. */
  formElements?: FormElement[];
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!noTrigger && (
        <DialogTrigger asChild>
          <TablePageHeaderActionButton icon={<ColumnsIcon />} label="Edit Columns" disabled={disabled} />
        </DialogTrigger>
      )}

      <DialogContent className="!top-1/2 flex max-h-[calc(100vh-200px)] !-translate-y-1/2 flex-col gap-[10px] overflow-hidden">
        <DialogTitle>Edit Columns</DialogTitle>

        <DialogDescription>
          You can control which fields are visible and what order they appear by toggling the visibility on/off and dragging to rearrange.
        </DialogDescription>

        <ColumnsDialogControls
          onOpenChange={onOpenChange}
          columns={columns}
          columnOrder={columnOrder}
          onColumnOrderChange={onColumnOrderChange}
          defaultSectionLabel={defaultSectionLabel}
          formElements={formElements}
        />
      </DialogContent>
    </Dialog>
  );
}

const SCROLL_SHADOW_GRADIENT = 'radial-gradient(ellipse 60% 80% at 50% var(--pos), rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.03) 50%, transparent 100%)';

function ScrollShadow({ position, visible }: { position: 'top' | 'bottom'; visible: boolean }) {
  return (
    <div
      className={cn(
        'pointer-events-none absolute right-0 left-0 z-10 h-6 transition-opacity duration-300',
        position === 'top' ? 'top-0' : 'bottom-0',
        visible ? 'opacity-100' : 'opacity-0',
      )}
      style={{ '--pos': position === 'top' ? '0%' : '100%', background: SCROLL_SHADOW_GRADIENT } as React.CSSProperties}
    />
  );
}

function ColumnsDialogControls<T>({
  onOpenChange,
  columns,
  columnOrder,
  onColumnOrderChange,
  defaultSectionLabel,
  formElements,
}: {
  onOpenChange: (open: boolean) => void;
  columns: ColumnDef<T>[];
  columnOrder: { id: string; active: boolean }[];
  onColumnOrderChange: (columnOrder: { id: string; active: boolean }[]) => void;
  defaultSectionLabel?: string;
  formElements?: FormElement[];
}) {
  // `columns` is the authoritative set (it always carries the form-field columns
  // once the form template loads); `columnOrder` supplies order + visibility but can
  // lag behind it. Reconcile against `columns` so a column missing from a stale
  // `columnOrder` is still shown (default hidden) instead of dropping out of the panel.
  const allColumnIds = useMemo(
    () => columns.map((col) => col.id ?? (col as { accessorKey?: string }).accessorKey).filter((id): id is string => !!id),
    [columns],
  );

  const reconciledColumns = useMemo<ModuleTableColumn[]>(() => {
    const validIds = new Set(allColumnIds);
    const orderedIds = new Set(columnOrder.map((col) => col.id));
    const ordered = columnOrder.filter((col) => validIds.has(col.id));
    const missing = allColumnIds.filter((id) => !orderedIds.has(id)).map((id) => ({ id, active: false }));
    return [...ordered, ...missing];
  }, [columnOrder, allColumnIds]);

  const selectColumn = useMemo(() => reconciledColumns.find((col) => col.id === 'select'), [reconciledColumns]);
  const columnsWithoutSelect = useMemo(() => reconciledColumns.filter((col) => col.id !== 'select'), [reconciledColumns]);

  const [tempColumns, setTempColumns] = useState<ModuleTableColumn[]>(columnsWithoutSelect);

  const tempColumnsRef = useRef(tempColumns);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  useEffect(() => {
    tempColumnsRef.current = tempColumns;
  }, [tempColumns]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const handleScroll = () => {
      setShowTopShadow(el.scrollTop > 0);
      setShowBottomShadow(el.scrollTop + el.clientHeight < el.scrollHeight);
    };

    handleScroll();
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  const groupRows = useMemo(
    () =>
      groupConditionalColumns(
        tempColumns.map((column) => column.id),
        formElements ?? [],
      ),
    [tempColumns, formElements],
  );
  const ruleGroups = useMemo(() => ruleGroupsByColumn(groupRows), [groupRows]);
  const topLevelColumnIds = useMemo(() => new Set(groupRows.flatMap((row) => (row.kind === 'field' && row.depth === 0 ? [row.columnId] : []))), [groupRows]);

  const [columnListRef, columnList] = useDragAndDrop<HTMLUListElement, ModuleTableColumn>(
    tempColumns.filter((column) => topLevelColumnIds.has(column.id)),
    {
      dragHandle: '.column-handle',
      onSort: ({ values }) => {
        const reorderedIds = (values as ModuleTableColumn[]).map((column) => column.id);
        setTempColumns(reorderWithinSlots(tempColumnsRef.current, reorderedIds));
      },
    },
  );

  const handleSave = () => {
    const columnById = new Map(tempColumns.map((column) => [column.id, column]));
    const groupedColumns = groupRows.flatMap((row) => {
      const column = row.kind === 'field' ? columnById.get(row.columnId) : undefined;
      return column ? [column] : [];
    });
    const finalColumns = selectColumn ? [selectColumn, ...groupedColumns] : groupedColumns;

    onColumnOrderChange(finalColumns);
    onOpenChange(false);
  };

  const findColumn = (columnId: string) => columns.find((c) => (c.id ? c.id === columnId : (c as { accessorKey: string }).accessorKey === columnId));

  const getColumnHeader = (columnId: string) => {
    const header = findColumn(columnId)?.header;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return typeof header === 'function' ? (header as (ctx: any) => string)({}) : header?.toString() || '';
  };

  const getSectionLabel = (columnId: string) => findColumn(columnId)?.meta?.columnGroup ?? defaultSectionLabel;

  const renderSectionLabel = (columnId: string) => {
    const label = getSectionLabel(columnId);
    return label ? (
      <Badge variant="grey" size="sm" className="font-medium">
        {label}
      </Badge>
    ) : null;
  };

  return (
    <div>
      <div className="relative">
        <ScrollShadow position="top" visible={showTopShadow} />
        <ScrollShadow position="bottom" visible={showBottomShadow} />
        {/* The `flex min-h-0 flex-1` scroll pattern in update-workspace-entity-dialog.tsx is more robust than this fixed viewport-offset cap and is preferred for new dialogs of this shape. */}
        <div ref={scrollContainerRef} className="max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
          <ul ref={columnListRef} className="columns">
            {columnList.map((column) => (
              <li key={column.id}>
                <ColumnRow
                  handleClassName="column-handle"
                  header={getColumnHeader(column.id)}
                  sectionLabel={renderSectionLabel(column.id)}
                  checkbox={<ColumnsDialogControlsCheckbox id={column.id} tempColumns={tempColumns} setTempColumns={setTempColumns} />}
                />
                <RuleGroupList
                  groups={ruleGroups.get(column.id) ?? []}
                  depth={0}
                  ruleGroups={ruleGroups}
                  getColumnHeader={getColumnHeader}
                  renderSectionLabel={renderSectionLabel}
                  tempColumns={tempColumns}
                  setTempColumns={setTempColumns}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="pt-3">
        <Button variant="success" className="w-fit" onClick={handleSave}>
          Save changes
        </Button>
      </div>
    </div>
  );
}

const indentFor = (depth: number) => `${depth * 24}px`;

function ColumnRow({
  handleClassName,
  depth = 0,
  header,
  sectionLabel,
  checkbox,
}: {
  handleClassName: string;
  depth?: number;
  header: string;
  sectionLabel: ReactNode;
  checkbox: ReactNode;
}) {
  return (
    <label
      className={cn(handleClassName, 'hover:bg-grey-1 flex cursor-pointer items-center justify-between gap-2 pr-2')}
      style={depth > 0 ? { paddingLeft: indentFor(depth) } : undefined}>
      <GripDotVerticalIcon className="text-secondary-text" />
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex items-center gap-1 p-[5px] text-sm">
          {header}
          {sectionLabel}
        </div>
        {checkbox}
      </div>
    </label>
  );
}

type RuleGroupListProps = {
  groups: RuleGroup[];
  depth: number;
  ruleGroups: Map<string, RuleGroup[]>;
  getColumnHeader: (columnId: string) => string;
  renderSectionLabel: (columnId: string) => ReactNode;
  tempColumns: ModuleTableColumn[];
  setTempColumns: (columns: ModuleTableColumn[]) => void;
};

function RuleGroupList({ groups, ...props }: RuleGroupListProps) {
  return (
    <>
      {groups.map((group) => (
        <div key={group.ruleId}>
          <div className="text-secondary-text flex items-center gap-1 py-1 text-sm" style={{ paddingLeft: indentFor(props.depth) }}>
            <LogicIcon aria-hidden />
            {`Value ${group.operator === 'is not' ? LogicRuleConfigLabels.operatorIsNot : LogicRuleConfigLabels.operatorIs} ${group.values.join(', ')}`}
          </div>
          {/* Keyed on its members so the drag list restarts from the new order after a sort. */}
          <RevealedFieldList key={group.columnIds.join()} columnIds={group.columnIds} {...props} depth={props.depth + 1} />
        </div>
      ))}
    </>
  );
}

/** A rule group's own drag list, so its fields can only be reordered among themselves. */
function RevealedFieldList({
  columnIds,
  depth,
  ruleGroups,
  getColumnHeader,
  renderSectionLabel,
  tempColumns,
  setTempColumns,
}: Omit<RuleGroupListProps, 'groups'> & { columnIds: string[] }) {
  const handleClassName = `revealed-field-handle-${depth}`;
  const tempColumnsRef = useRef(tempColumns);
  useEffect(() => {
    tempColumnsRef.current = tempColumns;
  }, [tempColumns]);

  const [listRef, list] = useDragAndDrop<HTMLUListElement, { id: string }>(
    columnIds.map((id) => ({ id })),
    {
      dragHandle: `.${handleClassName}`,
      onSort: ({ values }) => {
        setTempColumns(
          reorderWithinSlots(
            tempColumnsRef.current,
            (values as { id: string }[]).map((column) => column.id),
          ),
        );
      },
    },
  );

  return (
    <ul ref={listRef} className="columns">
      {list.map(({ id }) => (
        <li key={id}>
          <ColumnRow
            handleClassName={handleClassName}
            depth={depth}
            header={getColumnHeader(id)}
            sectionLabel={renderSectionLabel(id)}
            checkbox={<ColumnsDialogControlsCheckbox id={id} tempColumns={tempColumns} setTempColumns={setTempColumns} />}
          />
          <RuleGroupList
            groups={ruleGroups.get(id) ?? []}
            depth={depth}
            ruleGroups={ruleGroups}
            getColumnHeader={getColumnHeader}
            renderSectionLabel={renderSectionLabel}
            tempColumns={tempColumns}
            setTempColumns={setTempColumns}
          />
        </li>
      ))}
    </ul>
  );
}

function ColumnsDialogControlsCheckbox({
  id,
  tempColumns,
  setTempColumns,
}: {
  id: string;
  tempColumns: ModuleTableColumn[];
  setTempColumns: (columns: ModuleTableColumn[]) => void;
}) {
  const column = tempColumns.find((c) => c.id === id);
  if (!column) return null;

  return (
    <Checkbox
      className="size-3"
      checked={column.active}
      onCheckedChange={(checked) => setTempColumns(tempColumns.map((c) => (c.id === column.id ? { ...c, active: checked as boolean } : c)))}
    />
  );
}

export default ColumnsDialog;
